import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C_GOLD = "#C9A84C";
const C_DARK = "#0a1628";

/**
 * Página PÚBLICA — comprobante de atención.
 * Soporta dos rutas:
 *   /evidencia/:folio        → CRC/Oficio (lee de solicitudes)
 *   /evidencia-evento/:eventoId → Actividad ordinaria (lee de eventos_chofer)
 * No requiere login.
 */
export default function EvidenciaPublica() {
  const { folio, eventoId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [foto, setFoto] = useState(null);

  const identificador = folio || eventoId || "—";

  useEffect(() => {
    const url = folio
      ? `${API}/evidencia/${encodeURIComponent(folio)}`
      : `${API}/evidencia-evento/${encodeURIComponent(eventoId)}`;
    axios.get(url)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.status === 404
        ? "No hay evidencia de atención para este registro todavía."
        : (e.response?.data?.detail || "Error consultando evidencia")));
  }, [folio, eventoId]);

  const atendido = data?.estado === "ATENDIDO";
  const disponible = data?.comprobante_disponible;
  const mapUrl = (data?.lat != null && data?.lon != null)
    ? `https://www.google.com/maps?q=${data.lat},${data.lon}`
    : null;

  function _fecha(iso) {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString("es-MX", { dateStyle: "long", timeStyle: "short" });
    } catch { return iso; }
  }

  function Campo({ label, value, testId, children }) {
    if (!value && !children) return null;
    return (
      <div className="flex gap-2 text-sm" data-testid={testId}>
        <dt className="font-semibold min-w-[130px] shrink-0" style={{ color: "#64748B" }}>{label}:</dt>
        <dd className="text-slate-800">{children || value}</dd>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 px-3" style={{ background: "#F1F5F9" }}
         data-testid="page-evidencia-publica">
      <div className="max-w-2xl mx-auto rounded-xl shadow overflow-hidden"
           style={{ border: "1px solid #CBD5E1", background: "#fff" }}>

        {/* Header oficial */}
        <header className="px-5 py-4 flex items-center justify-between"
                style={{ background: C_DARK }}>
          <div>
            <div className="font-bold text-xl tracking-wide" style={{ color: C_GOLD }}>
              DMU 614
            </div>
            <div className="text-xs mt-0.5" style={{ color: "#94A3B8" }}>
              Comprobante público de atención · Municipio de Chihuahua
            </div>
          </div>
          <i className="fa-solid fa-shield-check text-3xl" style={{ color: C_GOLD, opacity: 0.9 }}/>
        </header>

        {/* Error: folio no existe */}
        {err && (
          <div className="p-8 text-center" data-testid="evidencia-no-encontrada">
            <i className="fa-regular fa-circle-question text-5xl text-slate-300 mb-3"/>
            <div className="text-lg font-semibold text-slate-700 mb-2">
              {folio ? <>Folio: <span className="font-mono">{identificador}</span></> : "Actividad no encontrada"}
            </div>
            <div className="text-slate-600">{err}</div>
            <div className="mt-4 text-xs text-slate-400">Verifica que el folio sea correcto.</div>
          </div>
        )}

        {data && (
          <div className="p-5 sm:p-6" data-testid="evidencia-body">

            {/* Folio */}
            {data.folio && (
              <div className="mb-4">
                <div className="text-xs font-bold tracking-wider uppercase" style={{ color: "#94A3B8" }}>Folio</div>
                <div className="font-mono text-3xl font-bold text-slate-900 mt-0.5"
                     data-testid="evidencia-folio">{data.folio}</div>
              </div>
            )}

            {/* Sello de estatus */}
            {disponible && (
              <div className="mb-5 p-4 rounded-lg flex items-center gap-3"
                   style={{
                     background: atendido ? "#ECFDF5" : "#FEF2F2",
                     border: `2px solid ${atendido ? "#10B981" : "#EF4444"}`,
                   }}
                   data-testid="evidencia-sello">
                <i className={`fa-solid ${atendido ? "fa-circle-check" : "fa-circle-xmark"} text-3xl`}
                   style={{ color: atendido ? "#047857" : "#991B1B" }}/>
                <div>
                  <div className="font-bold text-lg" style={{ color: atendido ? "#047857" : "#991B1B" }}>
                    {atendido ? "ATENDIDO" : "NO SE ATENDIÓ"}
                  </div>
                  {data.fecha_atencion && (
                    <div className="text-xs text-slate-600">{_fecha(data.fecha_atencion)}</div>
                  )}
                </div>
              </div>
            )}

            {/* En proceso */}
            {!disponible && (
              <div className="mb-5 p-4 rounded-lg flex items-start gap-3"
                   style={{ background: "#FFFBEB", border: "2px solid #FCD34D" }}
                   data-testid="evidencia-en-proceso">
                <i className="fa-solid fa-clock text-2xl mt-0.5" style={{ color: "#D97706" }}/>
                <div>
                  <div className="font-bold" style={{ color: "#92400E" }}>Reporte en proceso</div>
                  <div className="text-sm mt-1" style={{ color: "#78350F" }}>
                    Este reporte aún no ha sido atendido. Cuando el equipo lo atienda, aquí aparecerá el comprobante completo.
                  </div>
                </div>
              </div>
            )}

            {/* Datos del reporte */}
            <dl className="space-y-2">
              <Campo label="Origen" value={data.origen} testId="evidencia-origen"/>

              <Campo label="Lugar" testId="evidencia-lugar">
                {(data.lugar || data.direccion) && (
                  <span className="flex items-center gap-2 flex-wrap">
                    {data.lugar || data.direccion}
                    {mapUrl && (
                      <a href={mapUrl} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-xs font-semibold shrink-0"
                         style={{ color: "#0284C7" }}
                         data-testid="evidencia-mapa-link">
                        <i className="fa-solid fa-location-dot"/>Ver en mapa
                      </a>
                    )}
                  </span>
                )}
              </Campo>

              {data.lugar && data.direccion && data.lugar !== data.direccion && (
                <Campo label="Dirección" value={data.direccion} testId="evidencia-direccion"/>
              )}

              {/* GPS como link si no hay lugar pero sí coordenadas */}
              {!data.lugar && !data.direccion && mapUrl && (
                <Campo label="Coordenadas GPS" testId="evidencia-gps">
                  <a href={mapUrl} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-xs font-semibold"
                     style={{ color: "#0284C7" }}>
                    <i className="fa-solid fa-location-dot"/>
                    {data.lat?.toFixed(6)}, {data.lon?.toFixed(6)} — Ver en mapa
                  </a>
                </Campo>
              )}

              <Campo label="Estatus" testId="evidencia-estatus">
                <span className="font-mono text-xs font-bold px-2 py-0.5 rounded"
                      style={{
                        background: atendido ? "#D1FAE5" : data.estado === "PENDIENTE" ? "#FEF9C3" : "#FEE2E2",
                        color: atendido ? "#065F46" : data.estado === "PENDIENTE" ? "#713F12" : "#991B1B",
                      }}>
                  {data.estado}
                </span>
              </Campo>

              {data.nombre_ciudadano && (
                <Campo label="Ciudadano" value={data.nombre_ciudadano} testId="evidencia-ciudadano"/>
              )}
              {data.telefono_ciudadano && (
                <Campo label="Teléfono" testId="evidencia-telefono">
                  <a href={`tel:${data.telefono_ciudadano}`} style={{ color: "#0284C7" }}>
                    {data.telefono_ciudadano}
                  </a>
                </Campo>
              )}

              {data.capturado_por && (
                <Campo label="Capturado por" value={data.capturado_por} testId="evidencia-capturado-por"/>
              )}

              {data.pipa && (
                <Campo label="Pipa" testId="evidencia-pipa">
                  <span className="font-mono">{data.pipa}</span>
                </Campo>
              )}
              {data.chofer && (
                <Campo label="Atendido por" value={data.chofer} testId="evidencia-chofer"/>
              )}
              {data.companero && (
                <Campo label="Acompañante" value={data.companero} testId="evidencia-companero"/>
              )}
              {data.zona && (
                <Campo label="Zona" testId="evidencia-zona">
                  <span className="font-mono">{data.zona}</span>
                </Campo>
              )}

              {data.comentario && (
                <div className="mt-3 p-3 rounded" style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}
                     data-testid="evidencia-comentario">
                  <dt className="font-bold text-xs uppercase tracking-wide mb-1" style={{ color: "#475569" }}>Comentario</dt>
                  <dd className="text-sm text-slate-800">{data.comentario}</dd>
                </div>
              )}
              {!atendido && data.motivo && (
                <div className="mt-3 p-3 rounded" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}
                     data-testid="evidencia-motivo">
                  <dt className="font-bold text-xs uppercase tracking-wide mb-1" style={{ color: "#92400E" }}>Motivo</dt>
                  <dd className="text-sm text-slate-800">{data.motivo}</dd>
                </div>
              )}
            </dl>

            {/* Fotos de evidencia */}
            {data.fotos?.length > 0 && (
              <div className="mt-5">
                <div className="font-semibold text-sm mb-2" style={{ color: "#334155" }}>
                  <i className="fa-regular fa-images mr-1"/>
                  Foto de evidencia ({data.fotos.length})
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="evidencia-fotos">
                  {data.fotos.map((url, i) => {
                    const ext = (url.match(/\.(jpe?g|png|webp|gif)(?:\?|$)/i)?.[1] || "jpg").toLowerCase();
                    const fileBase = data.folio || identificador || "evidencia";
                    const fileName = `${fileBase}_evidencia_${i + 1}.${ext}`;
                    const downloadUrl = url.includes("/upload/")
                      ? url.replace("/upload/", `/upload/fl_attachment:${fileBase}_evidencia_${i + 1}/`)
                      : url;
                    return (
                      <div key={i} className="flex flex-col gap-2" data-testid={`evidencia-foto-wrap-${i}`}>
                        <button onClick={() => setFoto(url)}
                                className="block aspect-square rounded-lg overflow-hidden hover:opacity-90"
                                style={{ background: "#F1F5F9", border: "1px solid #CBD5E1" }}
                                data-testid={`evidencia-foto-${i}`}>
                          <img src={url} alt={`Evidencia ${i + 1}`} className="w-full h-full object-cover"/>
                        </button>
                        <a href={downloadUrl} download={fileName} target="_blank" rel="noreferrer"
                           className="inline-flex items-center justify-center gap-2 rounded-lg py-3 px-4 text-sm font-semibold transition-colors"
                           style={{ background: C_DARK, color: "#fff" }}
                           data-testid={`evidencia-descargar-${i}`}>
                          <i className="fa-solid fa-download"/>
                          Descargar foto {i + 1}
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {data.fotos?.length === 0 && disponible && (
              <div className="mt-4 p-3 rounded text-xs text-center"
                   style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", color: "#94A3B8" }}>
                <i className="fa-regular fa-image mr-1"/> Sin fotos de evidencia para este registro
              </div>
            )}
          </div>
        )}

        <footer className="px-5 py-3 text-xs text-center"
                style={{ borderTop: "1px solid #E2E8F0", background: "#F8FAFC", color: "#94A3B8" }}>
          Documento generado por el Sistema <strong style={{ color: "#475569" }}>DMU 614</strong> · Chihuahua, Chih.
        </footer>
      </div>

      {/* Lightbox */}
      {foto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 cursor-pointer"
             style={{ background: "rgba(0,0,0,0.85)" }}
             onClick={() => setFoto(null)} data-testid="evidencia-foto-lightbox">
          <img src={foto} alt="ampliada" className="max-w-full max-h-full rounded-md"/>
        </div>
      )}
    </div>
  );
}
